import type { MatchResult } from '../types';

/**
 * Convert a raw `source_channel` DB value to a human-readable display label.
 * Used in the Group-by-channel section headers and the filter panel dropdown.
 * Auto title-cases unknown values so new channels never display as raw snake_case.
 */
export function prettifyChannel(raw: string): string {
  const key = raw.toLowerCase().trim();
  const MAP: Record<string, string> = {
    linkedin:     'LinkedIn',
    linked_in:    'LinkedIn',
    naukri:       'Naukri',
    indeed:       'Indeed',
    github:       'GitHub',
    github_jobs:  'GitHub',
    gitlab:       'GitLab',
    discord:      'Discord',
    referral:     'Referral',
    agency:       'Agency',
    bulk_upload:  'Bulk Upload',
    bulk:         'Bulk Upload',
    internal:     'Internal',
    org_database: 'Org Database',
    website:      'Website',
    job_board:    'Job Board',
    career_site:  'Career Site',
    api:          'API',
  };
  return MAP[key] ?? raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export type MatchBand = 'strong_fit' | 'good_fit' | 'partial_fit' | 'not_recommended';

export const BAND_LABEL: Record<MatchBand, { label: string; color: string; barClass: string }> = {
  strong_fit: {
    label: 'Strong fit',
    color: 'text-teal-700 dark:text-teal-400',
    barClass: 'from-teal-600 to-cyan-500',
  },
  good_fit: {
    label: 'Good fit',
    color: 'text-sky-600 dark:text-sky-400',
    barClass: 'from-sky-500 to-blue-500',
  },
  partial_fit: {
    label: 'Possible fit',
    color: 'text-amber-600 dark:text-amber-400',
    barClass: 'from-amber-500 to-orange-400',
  },
  not_recommended: {
    label: 'Not recommended',
    color: 'text-slate-500 dark:text-slate-400',
    barClass: 'from-slate-500 to-slate-400',
  },
};

export function bandOf(score: number): MatchBand {
  if (score >= 75) return 'strong_fit';
  if (score >= 55) return 'good_fit';
  if (score >= 35) return 'partial_fit';
  return 'not_recommended';
}

export function bandCountsFor(matches: MatchResult[]): Record<MatchBand, number> {
  const c: Record<MatchBand, number> = {
    strong_fit: 0,
    good_fit: 0,
    partial_fit: 0,
    not_recommended: 0,
  };
  for (const m of matches) c[bandOf(m.composite_score)]++;
  return c;
}

/** 0–5 stars from composite 0–100 */
export function scoreToStars(score: number): number {
  return Math.min(5, Math.max(0, Math.round((Number(score) / 100) * 5)));
}

export function clientNameFromMatch(m: MatchResult): string {
  const j = m.job as unknown as { client?: { name?: string }; client_id?: string } | undefined;
  return j?.client?.name?.trim() || 'Client TBD';
}

export function jobTitleFromMatch(m: MatchResult): string {
  return m.job?.title?.trim() || 'Role TBD';
}

/** Same candidate, other jobs — excludes current row, highest composite first. */
export function otherMatchesForCandidate(current: MatchResult, allForCandidate: MatchResult[]): MatchResult[] {
  return allForCandidate
    .filter((x) => x.job_id !== current.job_id)
    .sort((a, b) => b.composite_score - a.composite_score);
}

/** Section title for alternate-job recommendations (engine: other scored JDs for this candidate). */
export function alternateRolesHeading(current: MatchResult): string {
  const b = bandOf(current.composite_score);
  if (b === 'not_recommended' || b === 'partial_fit') {
    return 'May be a better fit for other roles';
  }
  return 'Other open roles for this candidate';
}
